import { usePageAction } from '@cc-shopping/platform-analytics/hooks/usePageAction';
import { GrayDeselectableQuickFilters } from '@cc-shopping/ui/composites/GrayDeselectableQuickFilters';
import { Box, Divider } from '@mui/material';
import { useEffect, useState } from 'react';
import { CardMessage } from '../CardMessage';
import { useCompareContext } from '../Context';
import { type CardOfferButtonWithAnalytics, HorizontalScrollableCardList } from '../HorizontalScrollableCardList';
import useDragScroll from '../HorizontalScrollableCardList/useDragScroll';
import { COMPARE_DISPLAY_NAME_TO_SECTION_NAME, COMPARE_FILTERS_CATEGORY_DISPLAY_LIST, CompareSectionNames, compareQuickFiltersOnSelectTab } from './utils';
export type CategoryToOffersMetadata = {
  [key: string]: CardOfferButtonWithAnalytics[];
};
export interface Props {
  categoryToCardOfferButtons: CategoryToOffersMetadata;
  onClick?: (id: string) => void;
}
export const CompareQuickFilters = ({
  categoryToCardOfferButtons
}: Props) => {
  const [showPrevViewedHelpText, setShowPrevViewedHelpText] = useState(false);
  const [displayedCardOfferButtons, setDisplayedCardOfferButtons] = useState<CardOfferButtonWithAnalytics[]>([]);
  const {
    areSlotsOpen,
    isLoading,
    previouslyViewedIds
  } = useCompareContext();
  const {
    dragProps,
    isDragging
  } = useDragScroll();
  const fireClickCategoryPageAction = usePageAction({
    actionName: 'cc_compare_select_category',
    actionSection: 'cc_compare_category',
    actionType: 'select'
  });
  useEffect(() => {
    compareQuickFiltersOnSelectTab(0, categoryToCardOfferButtons, setDisplayedCardOfferButtons);
  }, []);
  const handleCategoryChange = (index: number | null) => {
    if (index !== null) {
      const categoryName = COMPARE_FILTERS_CATEGORY_DISPLAY_LIST[index];
      const categoryEnumValue = COMPARE_DISPLAY_NAME_TO_SECTION_NAME[categoryName];
      fireClickCategoryPageAction({
        action_input: categoryEnumValue
      });
      compareQuickFiltersOnSelectTab(index, categoryToCardOfferButtons, setDisplayedCardOfferButtons);
      setShowPrevViewedHelpText(categoryEnumValue === CompareSectionNames.previously_viewed && previouslyViewedIds.length === 0);
    }
    if (dragProps.ref.current) {
      dragProps.ref.current.scrollLeft = 0;
    }
  };
  return <>
      <Box width="100%" display="flex" flexDirection="column">
        <Box className="compare-quick-filters" sx={{
        ...(!areSlotsOpen && {
          pointerEvents: 'none',
          opacity: 0.25
        })
      }}>
          <GrayDeselectableQuickFilters filtersList={COMPARE_FILTERS_CATEGORY_DISPLAY_LIST} onSelectTab={handleCategoryChange} disabled={isLoading || !areSlotsOpen} />
        </Box>
        <Divider sx={{
        width: '100%',
        borderColor: 'neutral.lighter',
        alignSelf: 'center',
        mb: 4
      }} />
        <Box display="flex" justifyContent="center" sx={{
        overflowX: 'auto',
        msOverflowStyle: 'none',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': {
          display: 'none'
        }
      }}>
          <Box position="relative" minHeight="175px" className="scrollable-card-list">
            {!showPrevViewedHelpText && areSlotsOpen && <HorizontalScrollableCardList cardOfferButtons={displayedCardOfferButtons} dragProps={dragProps} isDragging={isDragging} />}
            {!areSlotsOpen && <CardMessage message="Please remove a card in order to select a different one" icon="removeCard" />}
            {showPrevViewedHelpText && <CardMessage message="Cards that you compared previously will appear here." icon="emptyCard" />}
          </Box>
        </Box>
      </Box>
    </>;
};